import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SideNavigation, {
  SideNavigationProps,
} from "@amzn/awsui-components-react/polaris/side-navigation";
import {
  DEFAULT_LEFT_NAV_PATH,
  LEFT_NAV_ITEMS,
  LeftNavPath,
  resolveLeftNavPath,
} from "./shellRoutes";
import { WIKI_LINK_URL } from "../../constants/common";

/** App Shell left nav. Items follow `LEFT_NAV_ITEMS` order. */

const HEADER_TEXT = "Devices ASIN Central";
const WIKI_EXTERNAL_ARIA_LABEL = " (opens in a new tab)";

export const LeftNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => resolveLeftNavPath(location.pathname),
    [location.pathname]
  );

  const items = useMemo<ReadonlyArray<SideNavigationProps.Item>>(() => {
    const primary: SideNavigationProps.Link[] = LEFT_NAV_ITEMS.map((item) => ({
      type: "link",
      text: item.label,
      href: item.path,
    }));

    const wikiLink: SideNavigationProps.Link = {
      type: "link",
      text: "Wiki",
      href: WIKI_LINK_URL,
      external: true,
      externalIconAriaLabel: WIKI_EXTERNAL_ARIA_LABEL,
    };

    return [...primary, { type: "divider" }, wikiLink];
  }, []);

  const handleFollow = useCallback(
    (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
      // Wiki opens in a new tab natively.
      if (event.detail.external) return;
      // Nav without page reload. Preserve `search` + `hash` so
      // the Applied Filter (encoded in the hash) survives.
      event.preventDefault();
      navigate({
        pathname: event.detail.href,
        search: window.location.search,
        hash: window.location.hash,
      });
    },
    [navigate]
  );

  return (
    <SideNavigation
      header={{ text: HEADER_TEXT, href: DEFAULT_LEFT_NAV_PATH }}
      items={items}
      activeHref={activePath}
      onFollow={handleFollow}
    />
  );
};

// Exported for unit tests that assert on the rendered Wiki link.
export const LEFT_NAV_HEADER_TEXT = HEADER_TEXT;
export const LEFT_NAV_WIKI_LINK_HREF = WIKI_LINK_URL;

export default LeftNav;
